/**
 * The organization's brand, read once at startup (ORDR-139).
 *
 * ordering serves it PUBLICLY (`api/brand.py`, ORDR-137) because the app paints
 * before anybody signs in: `GET /ordering/brand/?host=<host>` or
 * `?brand=<node_id>`, exactly one. A 200 carries `{ data: { display_name,
 * logo_url, primary, accent } }`, each omitted when unset; every miss is the
 * same 404.
 *
 * EVERY FAILURE IS `null`, AND `null` RENDERS NEUTRAL. A 404, a network failure
 * and a malformed body all land there, and nothing in this path ever reaches
 * for a platform brand. The 404 is silent (most hosts are unbranded); the rest
 * are logged with the correlation id a support ticket needs.
 *
 * A MALFORMED BODY IS REFUSED WHOLE. The server validates on write and again on
 * read, so a bad field here means the response is not the one ordering sent.
 * Keeping its other fields would be trusting a body already shown to be wrong.
 * The colour check is also the CSS-injection boundary: a colour becomes a custom
 * property value, and `red;background:url(//x)` must never get that far.
 * `resolveBrand` checks again downstream; this is the first guard, not the only.
 */

import type { BrandRecord, BrandTier } from "@pirouz-ca/ui-tokens";
import { isBrandHex } from "@pirouz-ca/ui-tokens";
import type { ApiClient } from "../api/client.js";
import { brandFromSearch } from "../session/address.js";

export type BrandLookup = { readonly kind: "host"; readonly host: string } | { readonly kind: "brand"; readonly nodeId: string };

/** Mirrors ordering's `DISPLAY_NAME_MAX_LENGTH` and `URL_MAX_LENGTH`. */
const DISPLAY_NAME_MAX = 80;
const URL_MAX = 2048;
/** Mirrors ordering's `_URL_REFUSED`: each can end a CSS `url(...)` or an HTML attribute. */
const URL_REFUSED = /["'()<>\\;`{}]/;
const PRINTABLE_ASCII = /^[!-~]+$/;

/** The `?brand=` pilot links carry wins; otherwise the host the app is served on. Never both. */
export function brandLookupFor(host: string, search: string): BrandLookup {
  const nodeId = brandFromSearch(search);
  return nodeId === null ? { kind: "host", host } : { kind: "brand", nodeId };
}

function isLogoUrl(value: string): boolean {
  return (
    value.length <= URL_MAX &&
    PRINTABLE_ASCII.test(value) &&
    !URL_REFUSED.test(value) &&
    value.toLowerCase().startsWith("https://")
  );
}

/** The record, or `null` when any field present is not what ordering would send. */
function parseTheme(data: unknown): BrandRecord | null {
  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    return null;
  }
  const { display_name: name, logo_url: logo, primary, accent } = data as Record<string, unknown>;
  const brand: BrandRecord = {};
  if (name !== undefined) {
    if (typeof name !== "string" || name.trim() === "" || name.length > DISPLAY_NAME_MAX) {
      return null;
    }
    brand.displayName = name.trim();
  }
  if (logo !== undefined) {
    if (typeof logo !== "string" || !isLogoUrl(logo)) {
      return null;
    }
    brand.logoUrl = logo;
  }
  if (primary !== undefined) {
    if (!isBrandHex(primary)) {
      return null;
    }
    brand.primaryColor = primary;
  }
  if (accent !== undefined) {
    if (!isBrandHex(accent)) {
      return null;
    }
    brand.accentColor = accent;
  }
  return brand;
}

export async function fetchBrandTier(client: ApiClient, lookup: BrandLookup): Promise<BrandTier | null> {
  const query =
    lookup.kind === "host"
      ? new URLSearchParams({ host: lookup.host })
      : new URLSearchParams({ brand: lookup.nodeId });
  const response = await client.request(`/ordering/brand/?${query.toString()}`);
  if (response.status === 404) {
    return null;
  }
  if (response.status !== 200) {
    console.warn("[brand] the brand read failed; rendering neutral", {
      status: response.status,
      correlationId: response.correlationId,
    });
    return null;
  }
  const envelope = response.body;
  const brand =
    typeof envelope === "object" && envelope !== null && "data" in envelope ? parseTheme(envelope.data) : null;
  if (brand === null) {
    console.warn("[brand] the brand read returned a malformed body; rendering neutral", {
      correlationId: response.correlationId,
    });
    return null;
  }
  return lookup.kind === "brand" ? { level: "enterprise", id: lookup.nodeId, brand } : { level: "enterprise", brand };
}
